/* eslint-disable no-lone-blocks */
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import RefreshIcon from '@mui/icons-material/Refresh';
import SearchIcon from '@mui/icons-material/Search';
import {
  Badge,
  Button,
  CustomDatePicker,
  Divider,
  Icon,
  Typography
} from 'components/atoms';
import { NewBreadCrumb } from 'components/molecules';
import { Pagination } from 'components/organisms';
import { DialogBox } from 'components/organisms/DialogBox';
import { ViewScreen } from 'components/template';
import { LeaveApplyModal } from 'components/template/LeaveApplyModal';
import { LeaveEditModal } from 'components/template/LeaveEditModal';
import { ApiResponseStatus } from 'models/enums';
import moment from 'moment';
import { useEffect, useState } from 'react';
import 'react-toastify/dist/ReactToastify.css';
import { RootState, useAppDispatch, useAppSelector } from 'store';
import {
  deleteLeaveRequest,
  filterLeaves,
  getLeaveSummary,
  getLeaveTypes,
  getPublicHolidays,
  requestLeaveCancellation,
  resetFilter,
  searchLeaves,
  sortLeaves
} from 'store/Leaves';
import './indiviaualLeaveInformation.scss';
// import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DriveFileRenameOutlineIcon from '@mui/icons-material/DriveFileRenameOutline';
import { IconButton } from '@mui/material';
import LogoSVG from 'components/atoms/LogoSVG';
import { LeaveSummaryCard } from 'components/organisms/LeaveSummaryCard';
import {
  Model,
  ModalBody,
  ModalHeader
} from 'components/atoms';
import { TextAreaProControlled } from 'components';
import {
  getEmployees,
  getStaffMemberMentorByStaffMemberId
} from 'store/MasterData';

export const IndividualLeaveInfo = () => {
  const dispatch = useAppDispatch();

  const leaves = useAppSelector((state: RootState) => {
    return state.leaves.summary ? state.leaves.summary.leaveSummary : null;
  });

  const masterDataStatus = useAppSelector((state: RootState) => {
    return state.leaves.masterDataStatus ? state.leaves.masterDataStatus : null;
  });

  // for getting requested, remaining, and total leave count of each leave type
  const allocatedLeaves = useAppSelector((state: RootState) => {
    return state.leaves.summary ? state.leaves.summary.allocationLeaves : null;
  });
  // for getting total leave count
  const totalLeaveCount = useAppSelector((state: RootState) => {
    return state.leaves.summary ? state.leaves.summary.totalLeaveCount : null;
  });
  // for getting total used leave count
  const totalUsedLeaveCount = useAppSelector((state: RootState) => {
    return state.leaves.summary ? state.leaves.summary.totalUsedLeaveCount : 0;
  });

  // for calculating the total number of leaves
  const [totalLeaves, setTotalLeaves] = useState(0);

  // for calculating the total number of leaves
  useEffect(() => {
    const calcTotalLeaves = () => {
      let total = 0;
      if (leaves) {
        leaves.forEach((leave) => {
          if (
            leave.leaveStatus !== 'Rejected' &&
            leave.leaveStatus !== 'Cancelled'
          )
            total += leave.noOfdays;
        });
        setTotalLeaves(total);
      }
    };

    calcTotalLeaves();
  }, [leaves]);

  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [, setOptionValue] = useState('');

  // for setting the status for loading spinner
  const [, setShowSpinner] = useState(false);

  // for deleting leave request
  const [confirm, setConfirm] = useState(false);
  const [open, setOpen] = useState(false);
  const [id, setId] = useState(0);

  // for cancellation request
  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelLeaveId, setCancelLeaveId] = useState('');
  const [cancelReason, setCancelReason] = useState('');
  const [cancelSubmitting, setCancelSubmitting] = useState(false);
  const { employees } = useAppSelector((state: RootState) => state.masterData);
  const { user } = useAppSelector((state: RootState) => state.auth);

  useEffect(() => {
    // dispatch the function for receiving individual leave information
    dispatch(getLeaveTypes());
    dispatch(getPublicHolidays());
    dispatch(getLeaveSummary());
    dispatch(getEmployees());
  }, [dispatch]);

  // Separate effect for staff member mentor lookup when employees data is available
  useEffect(() => {
    if (employees.length > 0 && user?.authData?.upn) {
      const currentEmployee = employees.find(
        (emp) => emp.employeeUpn === user.authData.upn
      );
      if (!currentEmployee) {
        console.warn('No employee found for the current user.');
      }
      if (currentEmployee?.employeeId) {
        dispatch(
          getStaffMemberMentorByStaffMemberId(currentEmployee.employeeId)
        );
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, employees.length, user?.authData?.upn]);

  const status = useAppSelector((state: RootState) => {
    return state.leaves.status ? state.leaves.status : null;
  });

  const filterByType = useAppSelector((state: RootState) => {
    return state.leaves.filterType ? state.leaves.filterType : null;
  });
  const startDate = useAppSelector((state: RootState) => {
    return state.leaves.startDate ? state.leaves.startDate : null;
  });
  const endDate = useAppSelector((state: RootState) => {
    return state.leaves.endDate ? state.leaves.endDate : null;
  });

  // newest filter method
  const setFilter = (value: any) => {
    dispatch(filterLeaves(value));
  };

  // newest search method
  const setSearch = (value: any) => {
    dispatch(searchLeaves(value));
  };

  // delete action dispatch
  const deleteLeave = (id: any) => {
    setId(id);
    setOpen(true);
  };

  const deleteFunction = async (id: any) => {
    await dispatch(deleteLeaveRequest(id));
    if (status === ApiResponseStatus.fulfilled) {
      await dispatch(getLeaveSummary());
      setShowSpinner(false);
    }
    setOpen(false);
  };

  const openCancelModal = (leaveId: string) => {
    setCancelLeaveId(leaveId);
    setCancelReason('');
    setCancelOpen(true);
  };

  const submitCancellation = async () => {
    if (!cancelLeaveId || !cancelReason.trim()) return;

    setCancelSubmitting(true);
    try {
      await dispatch(
        requestLeaveCancellation({
          leaveId: cancelLeaveId,
          reason: cancelReason.trim()
        })
      ).unwrap();
      await dispatch(getLeaveSummary());
      setCancelOpen(false);
    } catch {
      // toast handled in thunk
    } finally {
      setCancelSubmitting(false);
    }
  };

  const renderStatusBadge = (leaveStatus: string) => {
    if (leaveStatus === 'Approved') {
      return <Badge label="Approved" size="sm" type="success" />;
    }
    if (leaveStatus === 'Pending') {
      return <Badge label="Pending" size="sm" type="primary" />;
    }
    if (leaveStatus === 'CancellationPending') {
      return (
        <Badge
          label="Cancel pending"
          size="sm"
          type="warning"
          className="status-cancel-pending"
        />
      );
    }
    if (leaveStatus === 'Cancelled') {
      return <Badge label="Cancelled" size="sm" type="secondary" />;
    }
    return <Badge label="Rejected" size="sm" type="danger" />;
  };

  const headerItem1 = (
    <div>
      <Typography type="heading5" text="Aventude Pvt Ltd" />
      <NewBreadCrumb />
    </div>
  );

  const headerItem2 = <LeaveApplyModal />;

  const mainItem1 = (
    <>
      <div className="mainItem1_summary_cards">
        {totalLeaveCount && (
          <LeaveSummaryCard
            requestCount={totalUsedLeaveCount}
            totalCount={totalLeaveCount}
            type="summary_total"
            summaryTitle="Total Leave"
          />
        )}
        <div id="summary_each_type">
          {allocatedLeaves &&
            allocatedLeaves.map((leaveType: any) =>
              leaveType.leaveType !== 'Nopay Leave' ? (
                <LeaveSummaryCard
                  requestCount={leaveType.usedCount}
                  totalCount={leaveType.totalCount}
                  type={
                    leaveType.leaveType === 'Lieu Leave'
                      ? 'summary_lieu'
                      : leaveType.leaveType === 'Medical Leave'
                      ? 'summary_medical'
                      : leaveType.leaveType === 'Casual Leave'
                      ? 'summary_casual'
                      : leaveType.leaveType === 'Annual Leave'
                      ? 'summary_annual'
                      : 'summary_total'
                  }
                  summaryTitle={leaveType.leaveType}
                />
              ) : (
                <></>
              )
            )}
        </div>
      </div>
      <Divider orientation="hr" size="sm" />
      <div className="mainItem1">
        <div className="mainItem1_left">
          <div className="filter_by_leave_type_section">
            <Typography text="Filter by Leave Type" type="small_medium" />
            <select
              id="filter_by_type"
              className="leave_type_filter_section"
              value={filterByType || 'All'}
              onChange={(e) => {
                // using newest filter method
                setFilter({
                  filterByType: e.target.value,
                  startDate: startDate,
                  endDate: endDate
                });
              }}
            >
              <option value="All">All Leave types</option>
              <option value="Casual Leave">Casual</option>
              <option value="Medical Leave">Medical</option>
              <option value="Lieu Leave">Lieu</option>
              <option value="Annual Leave">Annual</option>
            </select>
          </div>
          <div className="date_range_filter_section">
            <CustomDatePicker
              labelText="From"
              onChange={(e) => {
                setFrom(e.target.value);
              }}
              defaultDate={from}
            />

            <CustomDatePicker
              labelText="To"
              onChange={(e) => setTo(e.target.value)}
              defaultDate={to}
            />

            <Button
              size="sm"
              type="secondary"
              disabled={from === '' || (to !== '' && from > to)}
              onClick={() => {
                // using the newest filter method
                setFilter({
                  filterByType: filterByType,
                  startDate:
                    from === ''
                      ? moment(new Date(0)).format('YYYY-MM-DD')
                      : from,
                  endDate:
                    to === ''
                      ? moment(new Date(8640000000000000)).format('YYYY-MM-DD')
                      : to
                });
              }}
            >
              Filter by date range
            </Button>
          </div>
          <Button
            disabled={
              (filterByType === 'All' || filterByType === null) && from === ''
            }
            type="secondary"
            size="sm"
            onClick={() => {
              dispatch(resetFilter());
              setFrom('');
              setTo('');
              setOptionValue('All');
            }}
          >
            <RefreshIcon className="reset_button" />
            Reset filtering
          </Button>
          <div className="gapping_element"></div>
          <Divider orientation="vr" size="lg" />
          <div className="search_by_descriptoin_section">
            <SearchIcon height={24} />
            <input
              type="text"
              placeholder="Search by description"
              onChange={(e) => {
                setSearch(e.target.value);
              }}
            />
          </div>
        </div>
        <div className={'mainItem1_right'}>
          <Icon type="primary" src="/icons/bars.svg" disabled />
          <Typography
            type="small_bold"
            text={
              totalLeaves === 1
                ? `${totalLeaves} Leave`
                : totalLeaves === null
                ? '0 Leave'
                : `${totalLeaves} Leave`
            }
          />
        </div>
      </div>
    </>
  );

  const columns = [
    { accessor: 'leaveType', label: 'Leave Type' },
    { accessor: 'reason', label: 'Leave Description' },
    { accessor: 'leaveSession', label: 'Leave Session' },
    { accessor: 'noOfdays', label: 'Applied Leaves' },
    { accessor: 'startDate', label: 'From' },
    { accessor: 'endDate', label: 'To' },
    { accessor: 'leaveStatus', label: 'Status' },
    { accessor: 'adminComment', label: 'HR Comments' },
    { accessor: 'action', label: 'Action' }
  ];

  const Table = (
    <div className="table_wrapper">
      <table>
        <thead>
          <tr>
            {columns.map((column) => {
              return (
                <th
                  key={column.accessor}
                  onClick={() => dispatch(sortLeaves(column.accessor))}
                >
                  <div className="th_padding">
                    <Typography type="small_bold" text={column.label} />
                    {column.accessor !== 'leaveType' &&
                    column.accessor !== 'action' ? (
                      <div>
                        <Icon
                          type="secondary"
                          src="/icons/angle_up.svg"
                          size="small"
                        />
                        <Icon
                          type="secondary"
                          src="/icons/angle_down.svg"
                          size="small"
                        />
                      </div>
                    ) : (
                      <div></div>
                    )}
                  </div>
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {leaves ? (
            leaves.map((row: any) => {
              return (
                <tr key={row.leaveId}>
                  {columns.map((column) => {
                    return column.accessor === 'action' &&
                      row['leaveStatus'] === 'Pending' ? (
                      <td className="td_space_between">
                        <LeaveEditModal props={row} />
                        <IconButton onClick={() => deleteLeave(row.leaveId)}>
                          <DeleteOutlineOutlinedIcon
                            fontSize="medium"
                            className="delete_icon"
                          />
                        </IconButton>
                      </td>
                    ) : column.accessor === 'action' &&
                      row['leaveStatus'] === 'Approved' ? (
                      <td className="td_space_between">
                        <Button
                          type="secondary"
                          size="sm"
                          className="cancel-leave-btn"
                          onClick={() => openCancelModal(row.leaveId)}
                        >
                          Cancel leave
                        </Button>
                      </td>
                    ) : column.accessor === 'action' &&
                      row['leaveStatus'] !== 'Pending' ? (
                      <td className="td_space_between">
                        <IconButton disabled={true}>
                          <DriveFileRenameOutlineIcon
                            fontSize="medium"
                            color="disabled"
                          />
                        </IconButton>
                        <IconButton disabled={true}>
                          <DeleteOutlineOutlinedIcon
                            fontSize="medium"
                            color="disabled"
                          />
                        </IconButton>
                      </td>
                    ) : column.accessor === 'leaveStatus' ? (
                      <td key={column.accessor}>
                        {renderStatusBadge(row[column.accessor])}
                      </td>
                    ) : column.accessor === 'startDate' ||
                      column.accessor === 'endDate' ? (
                      <td key={column.accessor}>
                        <Typography
                          type="small_regular"
                          text={moment(row[column.accessor]).format(
                            'MMMM DD YYYY'
                          )}
                        />
                      </td>
                    ) : (
                      <td key={column.accessor}>
                        <Typography
                          type="small_regular"
                          text={row[column.accessor]}
                        />
                      </td>
                    );
                  })}
                </tr>
              );
            })
          ) : (
            <tr></tr>
          )}
          <DialogBox
            titleMessage="Are you sure you want to delete this leave request?"
            confirm={confirm}
            setConfirm={setConfirm}
            setOpen={setOpen}
            open={open}
            handleConfirm={() => deleteFunction(id)}
          />
          <Model
            modalSize="sm"
            showModal={cancelOpen}
            onClose={() => setCancelOpen(false)}
          >
            <ModalHeader
              headerTitle="Request leave cancellation"
              onClose={() => setCancelOpen(false)}
            />
            <ModalBody>
              <Typography
                type="small_regular"
                text="Your project manager must approve first, then HR. Both must approve to cancel this leave. Please provide a reason."
              />
              <TextAreaProControlled
                label="Cancellation reason"
                name="cancelReason"
                placeholder="Reason for cancellation"
                value={cancelReason}
                onChange={setCancelReason}
                inputWrapperClass="comment-wrapper"
                inputClass="leave-request-comment-box"
              />
              <div style={{ display: 'flex', gap: '8px', marginTop: '16px' }}>
                <Button
                  type="secondary"
                  size="sm"
                  onClick={() => setCancelOpen(false)}
                  disabled={cancelSubmitting}
                >
                  Cancel
                </Button>
                <Button
                  type="primary"
                  size="sm"
                  onClick={submitCancellation}
                  disabled={cancelSubmitting || !cancelReason.trim()}
                >
                  {cancelSubmitting ? 'Submitting...' : 'Submit request'}
                </Button>
              </div>
            </ModalBody>
          </Model>
        </tbody>
      </table>
    </div>
  );

  return (
    <>
      {masterDataStatus === 'pending' && (
        // <Spinner showSpinner loadingMessage={'Loading, Please Wait...'} />
        <LogoSVG />
      )}
      {masterDataStatus === 'fulfilled' && (
        <ViewScreen
          headerItem1={headerItem1}
          headerItem2={headerItem2}
          mainItem1={mainItem1}
          mainItem2={Table}
          mainItem3={<Pagination />}
        />
      )}
    </>
  );
};
